import { useState } from 'react'
import { Form, Input, Button, Alert } from 'antd'
import { LoginOutlined, UserAddOutlined, LockOutlined, MailOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import type { LoginRequest } from '@/types'
import { CheckCircleFilled } from '@ant-design/icons'

const features = [
  'Ordini di lavoro e schede intervento',
  'Gestione clienti, veicoli e fornitori',
  'Magazzino gomme con QR code',
]

const LandingPage = () => {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onFinish = async (values: LoginRequest) => {
    setError(null)
    setLoading(true)
    try {
      await login(values)
    } catch (e: any) {
      if (e?.response?.status === 401) {
        setError('Email o password non corretti')
      } else if (e?.response?.status === 429) {
        setError('Troppi tentativi. Attendi qualche minuto e riprova.')
      } else {
        setError('Errore di connessione. Riprova.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="landing-split" style={{ display: 'flex', minHeight: '100vh' }}>

      {/* ── Pannello Brand ── */}
      <div className="landing-brand" style={{
        flex: '0 0 42%',
        background: '#0d1b2e',
        backgroundImage: [
          'radial-gradient(circle, rgba(24,144,255,0.18) 1px, transparent 1px)',
          'linear-gradient(180deg, rgba(24,144,255,0.06) 0%, transparent 60%)',
        ].join(', '),
        backgroundSize: '28px 28px, 100% 100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '48px 44px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: 0, left: 0, width: 4, height: '100%',
          background: 'linear-gradient(180deg, #1890ff 0%, rgba(24,144,255,0.2) 100%)',
        }} />
        <div>
          <img
            src="/Tiber-Garage.png"
            alt="Tiber Garage"
            style={{ height: 52, objectFit: 'contain', marginBottom: 48 }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
          <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#1890ff', marginBottom: 12 }}>
            Gestione Officina
          </div>
          <h1 style={{ fontSize: 38, fontWeight: 800, lineHeight: 1.1, color: '#ffffff', margin: '0 0 16px', letterSpacing: '-0.02em' }}>
            La tua officina,<br />
            <span style={{ color: 'rgba(255,255,255,0.45)', fontWeight: 300 }}>sotto controllo.</span>
          </h1>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, margin: '0 0 40px', maxWidth: 280 }}>
            Piattaforma professionale per meccanici e carrozzieri italiani.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {features.map((f) => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CheckCircleFilled style={{ color: '#1890ff', fontSize: 14, flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>{f}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Tiber Garage © {new Date().getFullYear()}
        </div>
      </div>

      {/* ── Pannello Login ── */}
      <div className="landing-actions" style={{
        flex: 1,
        background: 'var(--color-bg-container)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 32px',
      }}>
        <div style={{ width: '100%', maxWidth: 380 }}>
          <div style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 26, fontWeight: 700, color: 'var(--color-text)', margin: '0 0 8px', letterSpacing: '-0.01em' }}>
              Benvenuto
            </h2>
            <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', margin: 0 }}>
              Accedi al tuo account per continuare.
            </p>
          </div>

          {error && (
            <Alert type="error" message={error} showIcon style={{ marginBottom: 20 }} />
          )}

          <Form layout="vertical" onFinish={onFinish} size="large">
            <Form.Item
              name="username"
              label="Email"
              rules={[{ required: true, message: 'Inserisci la tua email' }]}
            >
              <Input
                prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
                placeholder="mario.rossi@example.com"
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Inserisci la password' }]}
              style={{ marginBottom: 8 }}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </Form.Item>

            <div style={{ textAlign: 'right', marginBottom: 20 }}>
              <Button
                type="link"
                size="small"
                style={{ padding: 0, color: 'var(--color-text-secondary)', fontSize: 13 }}
                onClick={() => navigate('/forgot-password')}
              >
                Password dimenticata?
              </Button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Button
                type="primary"
                icon={<LoginOutlined />}
                htmlType="submit"
                loading={loading}
                block
                style={{ height: 48, fontSize: 15, fontWeight: 600 }}
              >
                Accedi
              </Button>

              <Button
                icon={<UserAddOutlined />}
                block
                onClick={() => navigate('/register')}
                style={{ height: 48, fontSize: 15 }}
              >
                Richiedi accesso
              </Button>
            </div>
          </Form>

          <p style={{ fontSize: 12, color: '#bfbfbf', textAlign: 'center', marginTop: 32 }}>
            Accesso riservato al personale autorizzato
          </p>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .landing-split { flex-direction: column !important; }
          .landing-brand { flex: 0 0 auto !important; padding: 32px 24px !important; min-height: 240px; }
          .landing-brand h1 { font-size: 26px !important; }
          .landing-actions { padding: 32px 24px !important; }
        }
      `}</style>
    </div>
  )
}

export default LandingPage
